// /alerts route container (List archetype, alerts.md): owns the search-param
// read/navigate contract (`tab`/drawer subjects/history filters, validated by
// `alertsRoute`'s `validateSearch`), the four keyset list queries, and the
// six mutations. Rendering is delegated to props-only views under
// `./alerts/`; drawer-subject detail queries live in the drawer components.

import { useCallback, useEffect, useRef } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import type { components } from "../api/schema";
import {
  useResolvedWorkingTeamId,
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { MAX_PAGE_SIZE } from "../lib/pagination";
import { queryKeys } from "../lib/queryKeys";
import { useListQuery } from "../lib/useListQuery";
import {
  parseAlertsSearch,
  type AlertsSearch,
  type AlertsTab,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { GateRunDetail } from "./GateRunDetail";
import { Drawer, useToast } from "../ui";
import { AlertChannelDrawer } from "./alerts/AlertChannelDrawer";
import { AlertEventDrawer } from "./alerts/AlertEventDrawer";
import { AlertRuleDrawer } from "./alerts/AlertRuleDrawer";
import { AlertsView } from "./alerts/AlertsView";
import { NewAlertChannelDrawer } from "./alerts/NewAlertChannelDrawer";

type AlertChannelCreate = components["schemas"]["AlertChannelCreate"];
type AlertChannelPatch = components["schemas"]["AlertChannelPatch"];
type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertRulePatch = components["schemas"]["AlertRulePatch"];

// The full search this route sees: its own validated params plus the
// root-owned `?agent` scope.
type AlertsSearchState = AlertsSearch & WorkingScopeSearch;

export function Alerts() {
  const { agentId } = useWorkingScope();
  const authority = useCallerAuthority();
  const queryClient = useQueryClient();
  const toast = useToast();
  const routeNavigate = useNavigate();
  const navigate = useTypedNavigate<AlertsSearchState>();
  const search = useTypedSearch(withWorkingScopeSearch(parseAlertsSearch));
  const activeTab: AlertsTab = search.tab ?? "history";
  // Alert authoring is org-wide member+ in core: any active non-viewer
  // membership, or organization ownership, grants create/patch/delete.
  const canAuthor =
    authority.isOrgOwner ||
    authority.callerMemberships.some(
      (membership) => membership.member_role !== "viewer",
    );
  const availableChannelTypes = authority.alertChannelTypes;
  const canAuthorChannels = canAuthor && availableChannelTypes.length > 0;

  // Functional-merge navigation ONLY: patch this route's own params over the
  // previous search so the root-owned `?agent` scope always survives.
  // `replace: true` for drawer closes and mutation-success param clears so
  // Back never reopens a dismissed drawer (the ApiKeysSettings precedent);
  // opens and tab changes stay history pushes.
  const patchSearch = useCallback(
    (patch: Partial<AlertsSearch>, replace: boolean) => {
      void navigate({
        replace,
        search: mergeSearch<AlertsSearchState>(patch),
      });
    },
    [navigate],
  );

  // One drawer at a time (§4a): every open/switch clears the other subjects.
  const onTabChange = useCallback(
    (tab: AlertsTab) => {
      patchSearch(
        {
          tab,
          rule: undefined,
          channel: undefined,
          event: undefined,
          run: undefined,
          new: undefined,
        },
        false,
      );
    },
    [patchSearch],
  );
  const closeDrawers = useCallback(() => {
    patchSearch(
      {
        rule: undefined,
        channel: undefined,
        event: undefined,
        run: undefined,
        new: undefined,
      },
      true,
    );
  }, [patchSearch]);

  // A drawer subject never survives an agent-scope switch (the /markers
  // precedent): the lists refetch under the new agent_id, so an open
  // ?rule/?channel/?event/?run subject from the old scope is cleared
  // (replace — Back never restores the stale subject).
  const previousAgentIdRef = useRef(agentId);
  const subjectRuleId = search.rule;
  const subjectChannelId = search.channel;
  const subjectEventId = search.event;
  const subjectRunId = search.run;
  useEffect(() => {
    if (previousAgentIdRef.current === agentId) {
      return;
    }
    previousAgentIdRef.current = agentId;
    if (
      subjectRuleId !== undefined ||
      subjectChannelId !== undefined ||
      subjectEventId !== undefined ||
      subjectRunId !== undefined
    ) {
      patchSearch(
        {
          rule: undefined,
          channel: undefined,
          event: undefined,
          run: undefined,
        },
        true,
      );
    }
  }, [
    agentId,
    patchSearch,
    subjectChannelId,
    subjectEventId,
    subjectRuleId,
    subjectRunId,
  ]);

  // The deep-linked channel create drawer follows the same member+
  // affordance gate as edit/delete. Rule creation has its own route.
  useEffect(() => {
    if (
      search.new === "channel" &&
      !authority.isPending &&
      !authority.isError &&
      !canAuthorChannels
    ) {
      patchSearch({ new: undefined }, true);
    }
  }, [
    authority.isError,
    authority.isPending,
    canAuthorChannels,
    patchSearch,
    search.new,
  ]);

  // Scoped rail reads: `?agent=` is a real backend `agent_id` filter on all
  // four lists; All agents = the working team's rows (`team_id`,
  // docs/09 §4), held until the shell resolves the team. Agent wins: never
  // both filters. Filters ride inside the query keys.
  const workingTeamId = useResolvedWorkingTeamId();
  const scopeFilters =
    agentId != null ? { agent_id: agentId } : { team_id: workingTeamId };
  const scopeResolved = agentId != null || workingTeamId !== undefined;
  const gateRuns = useListQuery("/v1/gate-runs", {
    enabled: scopeResolved,
    params: scopeFilters,
    queryKey: queryKeys.gateRuns.list(scopeFilters),
  });
  const channels = useListQuery("/v1/alert-channels", {
    enabled: scopeResolved,
    params: scopeFilters,
    queryKey: queryKeys.alertChannels.list(scopeFilters),
  });
  const rules = useListQuery("/v1/alert-rules", {
    enabled: scopeResolved,
    params: scopeFilters,
    queryKey: queryKeys.alertRules.list(scopeFilters),
  });
  const events = useListQuery("/v1/alert-events", {
    enabled: scopeResolved,
    params: {
      ...scopeFilters,
      created_after: search.created_after,
      created_before: search.created_before,
      when: search.when,
    },
    queryKey: queryKeys.alertEvents.list({
      ...scopeFilters,
      created_after: search.created_after,
      created_before: search.created_before,
      when: search.when,
    }),
  });

  // Rule targets are organization-wide. The rail list above remains scoped
  // and user-paginated, while this dedicated form catalog is unfiltered and
  // follows the keyset to exhaustion for both create and edit.
  const ruleChannelOptions = useQuery({
    enabled: subjectRuleId !== undefined || subjectEventId !== undefined,
    queryKey: queryKeys.alertChannels.list({ surface: "rule-options" }),
    queryFn: async () => {
      const collected: AlertChannel[] = [];
      let pageToken: string | undefined;
      do {
        const { data, error } = await apiClient.GET("/v1/alert-channels", {
          params: {
            query: {
              page_size: MAX_PAGE_SIZE,
              page_token: pageToken,
            },
          },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        collected.push(...data.items);
        pageToken = data.next_page_token ?? undefined;
      } while (pageToken);
      return collected;
    },
  });

  const createChannel = useMutation({
    mutationFn: async (body: AlertChannelCreate) => {
      const { error } = await apiClient.POST("/v1/alert-channels", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Alert channel created");
      // Closing the create drawer unmounts the form, so its fields reset.
      patchSearch({ new: undefined }, true);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertChannels.all,
      });
    },
    onError: (error) => {
      toast.error("Alert channel create failed", {
        description: requestErrorDescription(error, "Alert request failed."),
      });
    },
  });

  const updateChannel = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: AlertChannelPatch;
    }) => {
      const { data, error } = await apiClient.PATCH("/v1/alert-channels/{id}", {
        body,
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (channel) => {
      queryClient.setQueryData(
        queryKeys.alertChannels.detail(channel.id),
        channel,
      );
      toast.success("Alert channel updated");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertChannels.all,
      });
    },
    onError: (error) => {
      toast.error("Alert channel update failed", {
        description: requestErrorDescription(error, "Alert request failed."),
      });
    },
  });

  const updateRule = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: AlertRulePatch }) => {
      const { data, error } = await apiClient.PATCH("/v1/alert-rules/{id}", {
        body,
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (rule) => {
      queryClient.setQueryData(queryKeys.alertRules.detail(rule.id), rule);
      toast.success("Alert rule updated");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertRules.all,
      });
    },
    onError: (error) => {
      toast.error("Alert rule update failed", {
        description: requestErrorDescription(error, "Alert request failed."),
      });
    },
  });

  const deleteChannel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/alert-channels/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Alert channel deleted");
      // Close the drawer so the deleted subject is never refetched into it.
      patchSearch({ channel: undefined }, true);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertChannels.all,
      });
    },
    onError: (error) => {
      // Keep the drawer's confirmation open so the operator can retry.
      toast.error("Alert channel delete failed", {
        description: requestErrorDescription(error, "Alert request failed."),
      });
    },
  });

  const deleteRule = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/alert-rules/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Alert rule deleted");
      patchSearch({ rule: undefined }, true);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.alertRules.all,
      });
    },
    onError: (error) => {
      // Keep the drawer's confirmation open so the operator can retry.
      toast.error("Alert rule delete failed", {
        description: requestErrorDescription(error, "Alert request failed."),
      });
    },
  });

  const ruleId = search.rule;
  const channelId = search.channel;

  return (
    <>
      <AlertsView
        activeTab={activeTab}
        authorityErrorMessage={authority.errorMessage}
        canAuthor={canAuthor}
        canAuthorChannels={canAuthorChannels}
        channels={channels}
        eventCreatedAfter={search.created_after}
        eventCreatedBefore={search.created_before}
        eventType={search.type}
        eventWhen={search.when}
        events={events}
        gateRuns={gateRuns}
        onOpenRun={(id) => {
          patchSearch(
            {
              run: id,
              rule: undefined,
              channel: undefined,
              event: undefined,
              new: undefined,
            },
            false,
          );
        }}
        onEventFiltersChange={(filters) => {
          patchSearch(
            {
              created_after: filters.created_after,
              created_before: filters.created_before,
              type: filters.type,
              when: filters.when,
            },
            false,
          );
        }}
        onOpenChannel={(id) => {
          patchSearch(
            {
              channel: id,
              event: undefined,
              rule: undefined,
              run: undefined,
              new: undefined,
            },
            false,
          );
        }}
        onOpenEvent={(id) => {
          patchSearch(
            {
              channel: undefined,
              event: id,
              rule: undefined,
              run: undefined,
              new: undefined,
            },
            false,
          );
        }}
        onOpenRule={(id) => {
          patchSearch(
            {
              rule: id,
              channel: undefined,
              event: undefined,
              run: undefined,
              new: undefined,
            },
            false,
          );
        }}
        onRetryAuthority={authority.retry}
        onStartCreate={(kind) => {
          patchSearch(
            {
              new: kind,
              rule: undefined,
              channel: undefined,
              event: undefined,
              run: undefined,
            },
            false,
          );
        }}
        onStartCreateRule={() => {
          void routeNavigate({
            search: (previous: WorkingScopeSearch) => previous,
            to: "/alerts/rules/new",
          });
        }}
        onTabChange={onTabChange}
        rules={rules}
      />
      <Drawer
        ariaLabel="Gate run detail"
        isOpen={subjectRunId !== undefined}
        onDismiss={closeDrawers}
        size="md"
      >
        {subjectRunId !== undefined ? (
          <GateRunDetail gateRunId={subjectRunId} onClose={closeDrawers} />
        ) : null}
      </Drawer>
      {ruleId !== undefined ? (
        <AlertRuleDrawer
          canEdit={canAuthor}
          channelOptionsErrorMessage={
            ruleChannelOptions.isError
              ? ruleChannelOptions.error.message
              : undefined
          }
          channels={ruleChannelOptions.data ?? []}
          editErrorMessage={
            updateRule.isError
              ? requestErrorDescription(
                  updateRule.error,
                  "Alert request failed.",
                )
              : undefined
          }
          isDeletePending={deleteRule.isPending}
          isChannelOptionsPending={ruleChannelOptions.isPending}
          isEditPending={updateRule.isPending}
          onDelete={() => {
            deleteRule.mutate(ruleId);
          }}
          onDismiss={closeDrawers}
          onEdit={async (body) => {
            await updateRule.mutateAsync({ body, id: ruleId });
          }}
          onEditReset={() => {
            updateRule.reset();
          }}
          onRetryChannelOptions={() => {
            void ruleChannelOptions.refetch();
          }}
          onSilence={() => {
            void routeNavigate({
              params: { ruleId },
              search: (previous: WorkingScopeSearch) => previous,
              to: "/alerts/rules/$ruleId/silence",
            });
          }}
          ruleId={ruleId}
        />
      ) : null}
      {channelId !== undefined ? (
        <AlertChannelDrawer
          availableChannelTypes={availableChannelTypes}
          canEdit={canAuthor}
          channelId={channelId}
          editErrorMessage={
            updateChannel.isError
              ? requestErrorDescription(
                  updateChannel.error,
                  "Alert request failed.",
                )
              : undefined
          }
          isDeletePending={deleteChannel.isPending}
          isEditPending={updateChannel.isPending}
          onDelete={() => {
            deleteChannel.mutate(channelId);
          }}
          onDismiss={closeDrawers}
          onEdit={async (body) => {
            await updateChannel.mutateAsync({ body, id: channelId });
          }}
          onEditReset={() => {
            updateChannel.reset();
          }}
        />
      ) : null}
      {subjectEventId !== undefined ? (
        <AlertEventDrawer
          channels={ruleChannelOptions.data ?? channels.items}
          eventId={subjectEventId}
          onDismiss={closeDrawers}
          rules={rules.items}
        />
      ) : null}
      {canAuthorChannels && search.new === "channel" ? (
        <NewAlertChannelDrawer
          availableChannelTypes={availableChannelTypes}
          isPending={createChannel.isPending}
          onDismiss={closeDrawers}
          onSubmit={(body) => {
            createChannel.mutate(body);
          }}
        />
      ) : null}
    </>
  );
}
